/**
 * Detects low-quality comments that could be replaced with emoji reactions.
 *
 * Based on https://github.com/refined-github/refined-github/blob/main/source/helpers/is-low-quality-comment.ts
 */

/** Multi-word phrases that indicate low-quality comments.
 * These are matched first before single words to handle phrases like "me too" correctly.
 */
export const LOW_QUALITY_PHRASES = ['me too', 'same here', 'same issue'] as const

/** Single words that indicate low-quality comments on their own.
 * This is intentionally a narrow, high-confidence list to avoid false positives.
 */
export const LOW_QUALITY_WORDS = ['bump', 'ditto', 'following', 'same', 'subscribe', 'subscribed'] as const

// Phrases must be matched first (longer patterns before shorter to avoid partial matches)
const lowQualityPhrasesRegex = new RegExp(
  String.raw`\b(${[...LOW_QUALITY_PHRASES].sort((a, b) => b.length - a.length).join('|')})\b`,
  'gi',
)
const lowQualityWordsRegex = new RegExp(String.raw`\b(${LOW_QUALITY_WORDS.join('|')})\b`, 'gi')

/** GitHub shortcode emoji that represent simple reactions (e.g., :+1:, :thumbsup:). */
const REACTION_SHORTCODES = [
  ':+1:',
  ':-1:',
  ':thumbsup:',
  ':thumbsdown:',
  ':clap:',
  ':pray:',
  ':ok_hand:',
  ':raised_hands:',
  ':100:',
  ':fire:',
  ':heart:',
  ':eyes:',
  ':rocket:',
  ':tada:',
]

/** Escapes special regex characters in a string. */
const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const shortcodeEmojiRegex = new RegExp(REACTION_SHORTCODES.map(escapeRegex).join('|'), 'gi')

/** Reaction emoji that can be used as simple reactions */
const REACTION_EMOJI = '👍👎👌🙏⬆️'
const reactionEmojiRegex = new RegExp(`[${REACTION_EMOJI}]`, 'gu')

/** Matches vote patterns like +1, -1, +100 */
const VOTE_PATTERN = '[+-]\\d+'
const votePatternRegex = new RegExp(VOTE_PATTERN, 'g')

/** Matches whitespace, punctuation, reaction emoji, skin tone modifiers, vote patterns (+1/-1) */
const nonSubstantiveContentRegex = new RegExp(
  `[\\s,.!?${REACTION_EMOJI}]+|[\\u{1F3FB}-\\u{1F3FF}]|${VOTE_PATTERN}`,
  'gu',
)

/**
 * Returns true if a comment could be replaced with a reaction (e.g., "+1", "me too", "👍").
 */
export function isLowQualityComment(text: string): boolean {
  if (!text || text.trim() === '') return false

  const remainingContent = text
    .replaceAll(lowQualityPhrasesRegex, '') // Match phrases first (e.g., "same here" before "same")
    .replaceAll(lowQualityWordsRegex, '')
    .replaceAll(shortcodeEmojiRegex, '')
    .replaceAll(nonSubstantiveContentRegex, '')

  return remainingContent === ''
}

/**
 * Returns the fragments that triggered low-quality detection.
 */
export function getLowQualityFragments(text: string): string[] {
  if (!text || text.trim() === '') return []

  const fragments: string[] = []

  // Get low-quality words
  const words = text.match(lowQualityWordsRegex) ?? []
  fragments.push(...words.map(w => w.toLowerCase()))

  // Get shortcode emoji
  const shortcodes = text.match(shortcodeEmojiRegex) ?? []
  fragments.push(...shortcodes.map(s => s.toLowerCase()))

  // Get vote patterns
  const votes = text.match(votePatternRegex) ?? []
  fragments.push(...votes)

  // Get reaction emoji
  const emoji = text.match(reactionEmojiRegex) ?? []
  fragments.push(...emoji)

  return [...new Set(fragments)]
}
